import { UniDriver } from '@wix/wix-ui-test-utils/dist/types/unidriver';
import { Simulate } from 'react-dom/test-utils';
import { act, fireEvent } from '@testing-library/react';

import { getFocus } from './helpers';
import { isReactLegacy, isReactNew } from './unidriver-guards';
import userEvent from '@testing-library/user-event';
import { uniDriverImplementation } from './unidriver-config';
import { safeGetNative } from '../components/internal/WixUiCoreComponents/test/unidriver';

const throwNotSupported = (): never => {
  throw new Error('not supported for this driver type');
};

const throwNotImplemented = (): never => {
  throw new Error('not implemented for this driver type');
};

type UniDriverMixin<T> = (base: UniDriver, _body?: UniDriver) => T;

export type Stylable = {
  hasStyleState: (stateName: string) => Promise<boolean>;
  select: (selector?: string) => Promise<Element>;
};

export const stylable =
  (stylesheet: any): UniDriverMixin<Stylable> =>
  (base) => {
    return {
      hasStyleState: async (stateName) => {
        const { StylableDOMUtil } = await import('@stylable/dom-test-kit');

        if (isReactLegacy(base) || isReactNew(base)) {
          const element = await base.getNative();
          const stylableUtil = new StylableDOMUtil(stylesheet, element);

          return stylableUtil.hasStyleState(element, stateName);
        }
        return throwNotSupported();
      },

      select: async (selector) => {
        const { StylableDOMUtil } = await import('@stylable/dom-test-kit');

        if (isReactLegacy(base) || isReactNew(base)) {
          const element = await base.getNative();
          const stylableUtil = new StylableDOMUtil(stylesheet, element);

          return stylableUtil.select(selector);
        }
        return throwNotSupported();
      },
    };
  };

export type PrivateDriver = {
  /** @private */
  _base: UniDriver;
};

export const privateMixin: UniDriverMixin<PrivateDriver> = (base) => {
  return {
    _base: base,
  };
};

export type FocusableDriver = {
  blur: () => Promise<void>;
  focus: () => Promise<void>;
  isFocus: () => Promise<boolean>;
};

export const focusable: UniDriverMixin<FocusableDriver> = (base) => {
  return {
    blur: async () => {
      const target = await base.getNative();
      if (isReactLegacy(base)) {
        return act(async () => Simulate.blur(target));
      } else if (isReactNew(base)) {
        return act(async () => {
          fireEvent.blur(target);
        });
      }
      return throwNotImplemented();
    },
    focus: async () => {
      const target = await base.getNative();
      if (isReactLegacy(base)) {
        return act(async () => Simulate.focus(target));
      } else if (isReactNew(base)) {
        return act(async () => {
          fireEvent.focus(target);
        });
      }

      return throwNotImplemented();
    },
    isFocus: async () => {
      const target = await base.getNative();
      if (isReactLegacy(base)) {
        return getFocus() === target;
      } else if (isReactNew(base)) {
        return getFocus() === target;
      }
      return throwNotImplemented();
    },
  };
};

export type ClickableDriver = {
  dblClick: () => Promise<void>;
  keyDown: (key?: string) => Promise<void>;
  keyUp: (key?: string) => Promise<void>;
};

export const clickable: UniDriverMixin<ClickableDriver> = (base) => {
  return {
    dblClick: async () => {
      if (isReactLegacy(base)) {
        return userEvent.dblClick(await base.getNative());
      } else if (isReactNew(base)) {
        return userEvent.dblClick(await base.getNative());
      }
      return throwNotImplemented();
    },
    keyDown: async (key?: string) =>
      act(async () => {
        const target = await base.getNative();

        switch (true) {
          case isReactNew(base): {
            fireEvent.keyDown(target, { key });
            return;
          }
          case isReactLegacy(base): {
            Simulate.keyDown(target, { key });
            return;
          }
          default: {
            throwNotImplemented();
          }
        }
      }),
    keyUp: async (key?: string) =>
      act(async () => {
        const target = await base.getNative();

        switch (true) {
          case isReactNew(base): {
            fireEvent.keyUp(target, { key });
            return;
          }
          case isReactLegacy(base): {
            Simulate.keyUp(target, { key });
            return;
          }
          default: {
            throwNotImplemented();
          }
        }
      }),
  };
};

export type InputableDriver = {
  isDisabled: () => Promise<boolean>;
  isRequired: () => Promise<boolean>;
  isChecked: () => Promise<boolean>;
  isReadOnly: () => Promise<boolean>;
};

export const inputable: UniDriverMixin<InputableDriver> = (base) => {
  return {
    isChecked: async () => {
      if (isReactLegacy(base)) {
        return ((await base.getNative()) as HTMLInputElement).checked;
      } else if (isReactNew(base)) {
        return ((await base.getNative()) as HTMLInputElement).checked;
      }
      return throwNotImplemented();
    },
    isRequired: async () => {
      if (isReactLegacy(base)) {
        return ((await base.getNative()) as HTMLInputElement).required;
      } else if (isReactNew(base)) {
        return ((await base.getNative()) as HTMLInputElement).required;
      }
      return throwNotImplemented();
    },
    isDisabled: async () => {
      if (isReactLegacy(base)) {
        return ((await base.getNative()) as HTMLInputElement).disabled;
      } else if (isReactNew(base)) {
        return ((await base.getNative()) as HTMLInputElement).disabled;
      }
      return throwNotImplemented();
    },
    isReadOnly: async () => {
      if (isReactLegacy(base)) {
        return ((await base.getNative()) as HTMLInputElement).readOnly;
      } else if (isReactNew(base)) {
        return ((await base.getNative()) as HTMLInputElement).readOnly;
      }
      return throwNotImplemented();
    },
  };
};

const hasRejected = (promise: Promise<any>) =>
  promise.then(
    () => false,
    () => true,
  );

export const getAsyncElementFinder = async (driver: UniDriver) => {
  let notFound = uniDriverImplementation.current(async () => null);
  notFound = (notFound as any).awaited?.(0) ?? notFound;

  const exists = await notFound.exists();

  const getNativeRejected = await hasRejected(notFound.getNative());

  const nestedGetNativeRejected = await hasRejected(
    notFound.$('jo').getNative(),
  );

  if (exists && !getNativeRejected && nestedGetNativeRejected) {
    if (await driver.exists()) {
      return driver.getNative();
    } else {
      throw new Error('React base driver - element was not found');
    }
  } else if (!exists && getNativeRejected && nestedGetNativeRejected) {
    return safeGetNative(driver);
  } else {
    throw new Error('Unexpected null uni driver method behaviour');
  }
};
